import gatewayService from '@/modules/common/services/gatewayService';

/**
 * Fetches offerings from the license API.
 * @param {string} params - Query string (e.g. "type=package&is_active=true")
 * @returns {Promise<{offerings: Array}>}
 */
const getOfferings = async (params) => gatewayService.get(`/v3/license/offerings?${params}`)
  .then((res) => res.data.data);

/**
 * Builds a query string for offerings API with default pagination and optional filters.
 * @param {Object} [filterPrams={}] - Optional filters (e.g. sku, is_active, sort). Falsy values are omitted.
 * @returns {string} Query string (e.g. "page=1&per_page=50&sku=foo")
 */
const buildQueryParams = (filterPrams = {}) => {
  const queryParams = new URLSearchParams({
    page: '1',
    per_page: '100',
  });

  Object.keys(filterPrams).forEach((key) => {
    if (filterPrams[key]) {
      queryParams.append(`${key}`, filterPrams[key]);
    }
  });

  return queryParams.toString();
};

/**
 * Fetches offerings from the subscription API. Returns offerings array or an error object on failure.
 * @param {Object} params
 * @param {string} [params.sku] - Filter by SKU
 * @param {string} [params.type] - Filter by offering type (e.g. 'package')
 * @param {boolean} [params.isActive] - Filter by active status
 * @param {string} [params.sort] - Sort option
 * @returns {Promise<Array|{code: string, message: string}>} Offerings array or { code: 'error', message }
 */
const fetchOfferings = async ({ sku, type, isActive, sort }) => {
  try {
    const queryString = buildQueryParams({ sku, type, is_active: isActive, sort });
    const response = await getOfferings(queryString);
    return response?.offerings;
  } catch (error) {
    console.error('Error fetching offering data:', error);
    return { code: 'error', message: error.message || 'An unknown error occurred.' };
  }
};

/**
 * Fetches offerings and returns the first one matching the given payment_type.
 * @param {Object} params
 * @param {string} [params.sku] - Filter by SKU
 * @param {boolean} [params.isActive] - Filter by active status
 * @param {string} [params.sort] - Sort option
 * @param {string} params.paymentType - Payment type to match (e.g. 'one_time', 'recurring')
 * @returns {Promise<Object|null>} Single offering object or null
 */
const fetchPurchasableOffering = async ({ sku, isActive, sort, paymentType }) => {
  const offerings = await fetchOfferings({ sku, isActive, sort });
  const validOfferings = offerings.filter((offering) => offering.payment_type === paymentType);
  return validOfferings[0] || null;
};

const fetchPurchasableOfferings = async ({
  sku, isActive, sort, paymentType,
}) => {
  const offerings = await fetchOfferings({ sku, isActive, sort });
  if (!Array.isArray(offerings)) return [];
  return offerings.filter((offering) => offering.payment_type === paymentType);
};

/**
 * Fetches the active, single-charge SMS offering.
 * @returns {Promise<Object|null>} Offering object or null when the catalogue has none
 */
const fetchSmsOffering = async () => fetchPurchasableOffering({
  sku: 'sms',
  isActive: true,
  sort: 'created_at:asc',
  paymentType: 'single_charge',
});

/**
 * Maps an offering's prices to an object keyed by currency, with display_name, item_uid, item_type merged.
 * @param {Object} offering - Offering with prices[], display_name, uid, type
 * @returns {Object.<string, Object>} Map of currency code -> { ...price, display_name, item_uid, item_type }
 */
const getOfferingPrices = (offering) => offering.prices.reduce((acc, obj) => {
  acc[obj.currency] = {
    ...obj,
    display_name: offering.display_name,
    item_uid: offering.uid,
    item_type: offering.type,
  };

  return acc;
}, {});

/**
 * @typedef {Object} OfferingService
 * @property {typeof fetchPurchasableOffering} fetchPurchasableOffering
 * @property {typeof fetchPurchasableOfferings} fetchPurchasableOfferings
 * @property {typeof getOfferingPrices} getOfferingPrices
 */
export default {
  getOfferings,
  fetchOfferings,
  fetchPurchasableOffering,
  fetchPurchasableOfferings,
  fetchSmsOffering,
  getOfferingPrices,
};
